import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import type { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";

const GetManagedEmployeesQuery = graphql(`
  query GetManagedEmployees($first: Int!, $after: String, $searchTerm: String) {
    managedEmployees(first: $first, after: $after, filter: { searchTerm: $searchTerm }) {
      edges {
        node {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const DEFAULT_PAGE_SIZE = 20;

export interface ManagedEmployeeOption {
  id: string;
  name: string;
}

interface UseManagedEmployeesOptions {
  limit?: number;
  searchTerm?: string;
}

export function useManagedEmployees(
  options: UseManagedEmployeesOptions = {},
): PaginatedDataAccessResult<ManagedEmployeeOption> {
  const { limit = DEFAULT_PAGE_SIZE, searchTerm } = options;

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: GetManagedEmployeesQuery,
    limit,
    variables: { searchTerm: searchTerm || undefined },
    connection: (data) => data.managedEmployees,
  });

  const filteredData = useMemo(() => data?.filter((node) => Boolean(node.id && node.name)), [data]);

  return {
    data: filteredData,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
